
import { phpApi } from "@/integrations/php/client";

declare global {
  interface Window {
    google: typeof google;
    initMap: () => void;
  }
}

let isLoading = false;
let isLoaded = false;
let cachedApiKey: string | null = null;
const callbacks: (() => void)[] = [];

async function fetchGoogleMapsApiKey(): Promise<string> {
  if (cachedApiKey) return cachedApiKey;

  try {
    const { data, error } = await phpApi.functions.invoke("get-google-maps-key", {
      body: {},
    });

    if (error || !data?.success || !data?.key) {
      console.error("Failed to fetch Google Maps API key:", error || data?.message);
      throw new Error("Google Maps API key not available");
    }

    cachedApiKey = data.key;
    return data.key;
  } catch (err) {
    console.error("Error fetching Google Maps API key:", err);
    throw err;
  }
}

export const loadGoogleMapsScript = (callback?: () => void): Promise<void> => {
  // If already loaded, call callback immediately
  if (isLoaded && window.google) {
    if (callback) callback();
    return Promise.resolve();
  }

  // Add to callbacks queue
  if (callback) {
    callbacks.push(callback);
  }

  // If already loading, wait for it
  if (isLoading) {
    return new Promise((resolve) => {
      callbacks.push(() => resolve());
    });
  }

  // Start loading
  isLoading = true;

  return new Promise(async (resolve, reject) => {
    try {
      // Fetch the API key from the database
      const apiKey = await fetchGoogleMapsApiKey();

      // Define callback function that will be called when Google Maps is loaded
      window.initMap = () => {
        isLoaded = true;
        isLoading = false;
        
        // Execute all callbacks
        callbacks.forEach(cb => cb());
        // Clear callbacks
        callbacks.length = 0;
        
        resolve();
      };

      // Create script element with async loading (recommended by Google)
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initMap&loading=async`;
      script.async = true;
      script.defer = true;
      script.onerror = (error) => {
        console.error('Google Maps script failed to load', error);
        isLoading = false;
        reject(new Error('Google Maps failed to load'));
      };

      document.head.appendChild(script);
    } catch (error) {
      isLoading = false;
      console.error('Error loading Google Maps script:', error);
      reject(error);
    }
  });
};

// Check if Google Maps is loaded
export const isGoogleMapsLoaded = (): boolean => {
  return isLoaded && !!window.google;
};
